import React from 'react';
import Skeleton from 'react-loading-skeleton';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import {
  ITEM_TYPE_HTML,
  ITEM_TYPE_PDF,
  ITEM_TYPE_VIDEO,
  ITEM_TYPE_QUIZ,
  styleVariables,
  LEARNING_MODE_COURSEWARE,
} from '../common/constants';
import VideoPlayer from '../components/videoPlayer';
import PDFViewer from '../components/pdf';
import HTML from '../components/html';
import Quiz from '../components/quiz';
import {
  getUserId, isAuthChecked,
} from '../store/userSlice';
import LoginModal from '../components/loginModal';

export const ContentLoader = ({ showLoader }) => (
  <div
    style={{ display: `${showLoader ? 'block' : 'none'}` }}
    className="contentLoader"
    key="contentLoader"
  >
    <h1>
      <Skeleton style={{ width: '40%' }} />
    </h1>
    <p>
      <Skeleton count={5} style={{ width: '80%' }} />
    </p>
    <p>
      <Skeleton count={7} style={{ width: '80%' }} />
    </p>
    <p>
      <Skeleton count={2} style={{ width: '80%' }} />
    </p>
    <h2>
      <Skeleton style={{ width: '50%' }} />
    </h2>
    <p>
      <Skeleton count={2} style={{ width: '80%' }} />
    </p>
    <p>
      <Skeleton count={5} style={{ width: '80%' }} />
    </p>
    <p>
      <Skeleton count={1} style={{ width: '80%' }} />
    </p>
  </div>
);

ContentLoader.propTypes = {
  showLoader: PropTypes.bool.isRequired,
};

export const UnitDetail = ({
  unit,
  unitId,
  showLoader,
  showTitle,
  tutorialData,
  curatedData,
  setHasQuizPassed,
  setHasVideoPlayed,
}) => {
  let moduleUri = null;
  const uid = useSelector(getUserId);
  const quizVisible = tutorialData?.learningMode === LEARNING_MODE_COURSEWARE || curatedData?.learningMode === LEARNING_MODE_COURSEWARE;
  const authChecked = useSelector(isAuthChecked);

  if (tutorialData && unitId) {
    moduleUri = tutorialData.uri;
  }
  if (curatedData && curatedData.unitLookup && unitId) {
    const moduleId = curatedData.unitLookup[unitId]?.parentId;
    if (moduleId) {
      moduleUri = `${curatedData.uri}/module/${moduleId}`;
    }
  }

  if (unit) {
    return (
      <RootElement className="contentLoader">
        <Helmet key="helmet">
          { tutorialData?.meta?.seo?.description ? <meta property="description" content={tutorialData.meta.seo.description} /> : <meta />}

          <meta property="og:type" content="article" />
          { tutorialData?.meta?.seo?.description ? <meta property="og:description" content={tutorialData.meta.seo.description} /> : <meta />}
          { tutorialData?.meta?.social?.image?.url ? <meta property="og:image" content={tutorialData.meta.social.image.url} /> : <meta />}
          { tutorialData?.meta?.social?.image?.height ? <meta property="og:image:height" content={tutorialData.meta.social.image.height} /> : <meta />}
          { tutorialData?.meta?.social?.image?.width ? <meta property="og:image:width" content={tutorialData.meta.social.image.width} /> : <meta />}

          <meta name="twitter:card" content="summary" />
          { tutorialData?.meta?.seo?.description ? <meta name="twitter:description" content={tutorialData.meta.seo.description} /> : <meta />}
          { tutorialData?.meta?.social?.image?.url ? <meta name="twitter:image" content={tutorialData.meta.social.image.url} /> : <meta />}
          <meta name="twitter:site" content="@autodesk" />
          <meta name="twitter:creator" content="@autodesk" />

        </Helmet>
        {showTitle && <h2 className="unitTitle h2">{unit.title}</h2>}
        {unit?.content?.map((content) => {
          switch (content?.type) {
          case ITEM_TYPE_VIDEO:
            return (
              <div className="unitItem" key={content.id}>
                <VideoPlayer
                  content={content || {}}
                  tutorialData={tutorialData}
                  curatedData={curatedData}
                  setHasVideoPlayed={setHasVideoPlayed}
                  unitId={unitId}
                  moduleUri={moduleUri}
                />
              </div>
            );
          case ITEM_TYPE_PDF:
            return (
              <div className="unitItem" key={content.id}>
                <PDFViewer data={content} />
              </div>
            );
          case ITEM_TYPE_HTML:
            return (
              <div className="unitItem htmlUnit" key={content.id}>
                <HTML content={content} />
              </div>
            );
          case ITEM_TYPE_QUIZ: {
            if (quizVisible) {
              return (
                <div className="unitItem" key={content.id}>
                  {content.graded ? (
                    <Helmet key="helmet">
                      <meta name="robots" content="noindex" />
                    </Helmet>
                  ) : (
                    <></>
                  )}
                  <Quiz
                    data={content}
                    curatedData={curatedData}
                    tutorialData={tutorialData}
                    unitId={unitId}
                    setHasQuizPassed={setHasQuizPassed}
                  />
                </div>
              );
            }
            return <></>;
          }
          default:
            return <></>;
          }
        })}

        {(authChecked && !uid && <LoginModal learningMode={tutorialData?.learningMode || curatedData?.learningMode} overviewUri={tutorialData?.uri || curatedData?.uri} />)}
      </RootElement>
    );
  }

  return <ContentLoader showLoader={showLoader} />;
};

UnitDetail.propTypes = {
  unit: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  unitId: PropTypes.string,
  curatedData: PropTypes.shape({
    uri: PropTypes.string,
    learningMode: PropTypes.string,
    unitLookup: PropTypes.arrayOf(PropTypes.shape({
      parentId: PropTypes.string,
    })),
  }),
  tutorialData: PropTypes.shape({
    id: PropTypes.string,
    uri: PropTypes.string,
    learningMode: PropTypes.string,
    dataSets: PropTypes.arrayOf(PropTypes.string),
    meta: PropTypes.shape({
      seo: PropTypes.shape({
        description: PropTypes.string,
      }),
      social: PropTypes.shape({
        image: PropTypes.shape({
          url: PropTypes.string,
          height: PropTypes.string,
          width: PropTypes.string,
        }),
      }),
    }),
  }),
  showLoader: PropTypes.bool,
  showTitle: PropTypes.bool,
  unitCompleteRef: PropTypes.shape({}).isRequired,
  setHasVideoPlayed: PropTypes.func.isRequired,
  setHasQuizPassed: PropTypes.func.isRequired,
};

UnitDetail.defaultProps = {
  curatedData: null,
  tutorialData: null,
  showLoader: false,
  showTitle: false,
  unitId: undefined,
};

const RootElement = styled.div`
  .MuiGrid-root.MuiGrid-container{
    padding-left: 0;
    padding-right: 0;
  }
  h2.unitTitle {
    margin-bottom: 32px;
  }


  .htmlUnit {
    font-size: 16px;

    ul, ol {
      list-style-position: outside;
      padding: 0;
      margin: 0 0 0 1.5em;

      li {
        margin: 0.5rem 0;
      }
    }

    h3,
    h4 {
      margin-bottom: 10px;
    }

    h3 > strong > span {
      color: ${styleVariables.inkOnBackground} !important;
    }
  }

  @media only screen and (max-width: 700px) {
    .htmlUnit {
      max-width: 100%;
    }
  }
`;
